import type { EnhancedScopeData, LabelFormattingInfo } from '@ardoq/data-model';
import type { FormatUserFieldFn } from '@ardoq/scope-data';
import {
  APIEntityType,
  ArrowType,
  LineType,
  type APIFieldAttributes,
  type APIReferenceType,
  type ArdoqId,
  type GroupingRule,
  type ViewIds,
} from '@ardoq/api-types';
import {
  buildGraphFromTraversalAndSearch,
  formatOtherLabel,
  getAllReferenceLabelFilters,
  getGraphEdgeLabelForViewpointMode,
  getGraphInterfaceForEnhancedScopeData,
  getGraphNodeOrGroupLabelForViewpointMode,
  getIsShowReferenceTypeApplied,
  getLabelsInterfaceForEnhancedScopeData,
  getUrlFieldValuesByComponentId,
  ParentChildGraphEdge,
  type GetLabelsDataInterface,
  type ItemLabels,
  type LabelsContext,
  type LoadedGraphWithViewpointMode,
  type RawGraphEdge2,
  type RawGraphNode2,
  type UrlFieldValue,
} from '@ardoq/graph';
import { uniq } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import { groupingRuleOperations } from '@ardoq/core';
import { enhancedScopeDataOperations, referenceUtils } from '@ardoq/scope-data';
import { colors } from '@ardoq/design-tokens';
import type { I18nLocale } from '@ardoq/i18n';
import type {
  BlocksGraph,
  BlocksNode,
  BlocksReference,
} from 'tabview/visualizations/aq-blocks/types';
import { BlocksVisualState } from 'tabview/visualizations/aq-blocks/types';
import type { ModelIdStyle } from 'tabview/assistantShared/buildStyleByModelId';
import { resolveColor } from 'tabview/assistantShared/legend';
import { isGroupCollapsed } from './collapsedGroups';

/** What `getLabelsInterfaceForEnhancedScopeData` accepts as the Labels setting. */
export type LabelFormattingFilter = LabelsContext['formattingFilters'][number];

const NO_COMPONENT_FIELDS: APIFieldAttributes[] = [];

const UNIFORM_COLOR = colors.grey300;
const EDGE_COLOR = colors.black950;
const ROOT_ID = '__blocks_root__';

/**
 * How groups (and the root) are laid out:
 * - `manual`: the group carries `layoutType: 'manual'`; the caller assigns each
 *   child's grid `layoutData` afterwards (the AI-driven Assistant Blocks view).
 * - `engine`: the group is left for aq-blocks' own layout engine — `layoutType`
 *   stays undefined and the group is marked dirty so it gets laid out.
 */
export type GroupLayoutMode = 'manual' | 'engine';

export type BuildBlocksGraphArgs = {
  loadedGraph: LoadedGraphWithViewpointMode;
  styleByModelId: Map<ArdoqId, ModelIdStyle>;
  groupingRules: GroupingRule[];
  /** The Labels view setting; drives the sublabels below each node's name. */
  formattingFilters: LabelFormattingFilter[];
  /** Resolves `User` field values to a display name; they store a user id. */
  formatUserFieldFn: FormatUserFieldFn;
  i18nLocale: I18nLocale;
  groupLayoutMode: GroupLayoutMode;
  /**
   * Push each edge onto its endpoints' `links`. aq-blocks' layout engine reads
   * `node.links` (not `graph.edges`) to build the graph it lays out, so engine
   * layout requires this; manual layout does not.
   */
  linkEdgesToNodes: boolean;
  viewId: ViewIds;
  /**
   * Which groups are drawn closed - the persisted view setting, wildcard for all.
   *
   * The root is never one of them: it is synthetic and closing it would draw an empty diagram.
   */
  collapsedGroupIds: readonly string[];
};

export type BuildBlocksGraphResult = {
  graph: BlocksGraph;
  /**
   * The components the graph drew, including the component-backed group
   * containers, so the legend can describe a container whose component the
   * loaded graph pruned. Grouping resolves parents against the pre-collapse
   * scope, so those two sets differ.
   *
   * Deduped, unlike `referenceTypes`: grouping can emit several nodes for one
   * `modelId`, and the repeats carry nothing a caller could use.
   */
  componentIds: ArdoqId[];
  /**
   * The reference types the graph drew, in first-seen order and with repeats —
   * `toLegendProps` dedupes. Derived from the edges rather than the scope data,
   * because a grouping rule consumes the references it groups by and grouping by
   * parent turns parent-child relations into nesting: in both cases the type is
   * declared but no edge exists.
   *
   * The converse is not fully covered: an edge whose reference type cannot be
   * resolved is drawn with `DEFAULT_EDGE_STYLE` but contributes no type here, so
   * it goes undescribed. Naming that row is a product decision, and the
   * modernized Block Diagram omits it too.
   */
  referenceTypes: APIReferenceType[];
};

type NodeFields = Omit<BlocksNode, 'id' | 'parent' | 'children'>;

/**
 * `getComponentFields`, skipped when no component rule can read the result.
 * `getOtherLabelsDataForComponent` fetches the fields before deciding whether any
 * rule is relevant, and `getComponentFields` is an `Object.values` per node.
 *
 * Deliberately only the entity-type half of `getRelevantLabelFormatting`'s
 * filter: with no component rule it provably returns null, which needs no
 * knowledge of how it matches. Narrowing further by `typeName` would restate
 * that predicate across a package boundary, where drift shows up as chips
 * silently missing rather than as a slower build. That belongs in the upstream
 * early return, which has the type name already.
 */
const toComponentFieldsReader = (
  labelsInterface: Pick<GetLabelsDataInterface, 'getComponentFields'>,
  rules: LabelFormattingInfo[]
): GetLabelsDataInterface['getComponentFields'] =>
  rules.some(rule => rule.type === APIEntityType.COMPONENT)
    ? labelsInterface.getComponentFields
    : () => NO_COMPONENT_FIELDS;

const baseNodeFields = (): NodeFields => ({
  modelId: null,
  isComponent: false,
  links: [],
  representationData: null,
  isContext: false,
  color: UNIFORM_COLOR,
  labels: [''],
  labelColors: [],
  urlFieldValues: null,
  displayLabels: [],
  width: 150,
  height: 150,
  layoutType: undefined,
  bounds: [0, 0, 1, 1],
  visualState: BlocksVisualState.None,
});

const groupLayoutFields = (
  groupLayoutMode: GroupLayoutMode
): Partial<BlocksNode> =>
  groupLayoutMode === 'manual'
    ? { layoutType: 'manual' }
    : { isLayoutDirty: true };

type NodeLabelContext = {
  labelsInterface: GetLabelsDataInterface;
  i18nLocale: I18nLocale;
};

/**
 * `[mainLabel, subLabel, ...multilabels]` — the shape aq-blocks wraps and draws:
 * index 0 is the name, index 1 the submain slot, 2 upwards the sublabel chips.
 *
 * `BlocksItem.labels` is shared by nodes and references, so both fill the same slots.
 * A reference has no submain label, and holds the slot empty rather than closing it:
 * a renderer reading the chips from index 2 has to mean the same thing for both.
 */
const toItemLabels = ({
  mainLabel,
  subLabel,
  otherLabels,
}: ItemLabels): string[] => [
  mainLabel?.trim() ?? '',
  subLabel?.trim() ?? '',
  ...(otherLabels ?? []).map(formatOtherLabel),
];

const toNodeLabels = (
  { labelsInterface, i18nLocale }: NodeLabelContext,
  rawItem: RawGraphNode2
): string[] =>
  toItemLabels(
    getGraphNodeOrGroupLabelForViewpointMode(
      labelsInterface,
      rawItem,
      i18nLocale
    )
  );

type EdgeLabelContext = NodeLabelContext & {
  /**
   * Whether the Reference label setting asks for the type name, resolved from the same
   * filters and by the same predicate as the modernized Block Diagram. With it off,
   * `getGraphEdgeLabelForViewpointMode` returns an empty main label and only the
   * multilabel chips remain.
   */
  isShowReferenceTypeApplied: boolean;
};

const toEdgeLabels = (
  { labelsInterface, i18nLocale, isShowReferenceTypeApplied }: EdgeLabelContext,
  rawReference: RawGraphEdge2
): string[] =>
  toItemLabels(
    getGraphEdgeLabelForViewpointMode(
      labelsInterface,
      rawReference,
      i18nLocale,
      isShowReferenceTypeApplied
    )
  );

type BuildGroupNodeArgs = {
  styleByModelId: Map<ArdoqId, ModelIdStyle>;
  rawGroup: RawGraphNode2;
  groupLayoutMode: GroupLayoutMode;
  labelContext: NodeLabelContext;
  collapsedGroupIds: readonly string[];
};

const buildGroupNode = ({
  styleByModelId,
  rawGroup,
  groupLayoutMode,
  labelContext,
  collapsedGroupIds,
}: BuildGroupNodeArgs): BlocksNode => {
  const style = rawGroup.modelId
    ? styleByModelId.get(rawGroup.modelId)
    : undefined;
  return {
    ...baseNodeFields(),
    id: rawGroup.id,
    modelId: rawGroup.modelId ?? null,
    parent: null,
    children: [],
    color: style?.color ?? UNIFORM_COLOR,
    representationData: style?.representationData ?? null,
    labels: toNodeLabels(labelContext, rawGroup),
    open: !isGroupCollapsed(rawGroup.id, collapsedGroupIds),
    ...groupLayoutFields(groupLayoutMode),
  };
};

type BuildComponentNodeArgs = {
  styleByModelId: Map<ArdoqId, ModelIdStyle>;
  rawItem: RawGraphNode2;
  urlFieldValuesByComponentId: Map<ArdoqId, UrlFieldValue[] | null>;
  labelContext: NodeLabelContext;
};

const buildComponentNode = ({
  styleByModelId,
  rawItem,
  urlFieldValuesByComponentId,
  labelContext,
}: BuildComponentNodeArgs): BlocksNode => {
  const componentId = rawItem.modelId ?? rawItem.id;
  const style = styleByModelId.get(componentId);
  return {
    ...baseNodeFields(),
    id: rawItem.id,
    modelId: componentId,
    parent: null,
    children: null,
    isComponent: true,
    representationData: style?.representationData ?? null,
    color: style?.color ?? UNIFORM_COLOR,
    labels: toNodeLabels(labelContext, rawItem),
    urlFieldValues: urlFieldValuesByComponentId.get(componentId) ?? null,
  };
};

type EdgeStyle = Pick<
  BlocksReference,
  'lineBeginning' | 'lineEnding' | 'lineType' | 'color'
>;

const DEFAULT_EDGE_STYLE: EdgeStyle = {
  lineBeginning: ArrowType.NONE,
  lineEnding: ArrowType.NONE,
  lineType: LineType.SOLID,
  color: EDGE_COLOR,
};

const toEdgeStyle = (referenceType: APIReferenceType): EdgeStyle => ({
  lineBeginning: referenceType.lineBeginning ?? ArrowType.NONE,
  lineEnding: referenceType.lineEnding ?? ArrowType.NONE,
  lineType: referenceType.line ?? LineType.SOLID,
  color: resolveColor(referenceType),
});

const toReferenceType = (
  scopeData: EnhancedScopeData,
  modelId: ArdoqId
): APIReferenceType | null => {
  const reference = scopeData.referencesById[modelId];
  if (!reference) return null;
  return (
    enhancedScopeDataOperations.getReferenceType(scopeData, reference) ?? null
  );
};

/**
 * Builds the aq-blocks `BlocksGraph` (grouped nodes + reference edges) from a loaded
 * viewpoint graph, shared by Assistant Blocks and Flexible Block Diagram. The two
 * differ only in how groups are laid out (`groupLayoutMode`) and whether edges are
 * linked to their endpoint nodes for the layout engine (`linkEdgesToNodes`). Manual
 * layout of children (grid positions) is left to the caller.
 */
export const buildBlocksGraph = ({
  loadedGraph,
  styleByModelId,
  groupingRules,
  formattingFilters,
  formatUserFieldFn,
  i18nLocale,
  groupLayoutMode,
  linkEdgesToNodes,
  viewId,
  collapsedGroupIds,
}: BuildBlocksGraphArgs): BuildBlocksGraphResult => {
  const { scopeData } = loadedGraph;
  const validGroupingRules = groupingRules.filter(
    groupingRuleOperations.isValidRule
  );

  const graphInterface = getGraphInterfaceForEnhancedScopeData({
    enhancedScopeData: scopeData,
    conditionalFormatting: [],
    groupingRules: validGroupingRules,
    i18nLocale,
  });

  const { groupMap, componentMap, referenceList } =
    buildGraphFromTraversalAndSearch({
      graphInterface,
      graph: scopeData.graphData,
      groupingRules: validGroupingRules,
      viewId,
      loadedGraph,
      i18nLocale,
    });

  const root: BlocksNode = {
    ...baseNodeFields(),
    id: ROOT_ID,
    parent: null,
    children: [],
    open: true,
    ...groupLayoutFields(groupLayoutMode),
  };

  // Nothing traversed — an empty scope, or a graph model the traversal rejected.
  // Return before the label and URL-field passes, which would otherwise walk the
  // whole scope to build values only the node builders read.
  if (groupMap.size === 0 && componentMap.size === 0) {
    return { graph: { root, edges: [] }, referenceTypes: [], componentIds: [] };
  }

  const scopeDataLabelsInterface = getLabelsInterfaceForEnhancedScopeData({
    enhancedScopeData: scopeData,
    formattingFilters,
    formatUserFieldFn,
    i18nLocale,
  });
  const labelFormattingRules =
    scopeDataLabelsInterface.getLabelFormattingRules();
  const labelsInterface = {
    ...graphInterface,
    ...scopeDataLabelsInterface,
    getLabelFormattingRules: () => labelFormattingRules,
    getComponentFields: toComponentFieldsReader(
      scopeDataLabelsInterface,
      labelFormattingRules
    ),
  };

  const urlFieldValuesByComponentId = getUrlFieldValuesByComponentId(
    labelsInterface,
    loadedGraph.scopeComponentIds
  );

  const labelContext: NodeLabelContext = { labelsInterface, i18nLocale };
  const edgeLabelContext: EdgeLabelContext = {
    ...labelContext,
    isShowReferenceTypeApplied: getIsShowReferenceTypeApplied(
      getAllReferenceLabelFilters(formattingFilters)
    ),
  };

  const nodesById = new Map<string, BlocksNode>();
  for (const rawGroup of groupMap.values()) {
    nodesById.set(
      rawGroup.id,
      buildGroupNode({
        collapsedGroupIds,
        styleByModelId,
        rawGroup,
        groupLayoutMode,
        labelContext,
      })
    );
  }
  for (const rawItem of componentMap.values()) {
    nodesById.set(
      rawItem.id,
      buildComponentNode({
        styleByModelId,
        rawItem,
        urlFieldValuesByComponentId,
        labelContext,
      })
    );
  }

  const parentOf = (rawItem: RawGraphNode2): BlocksNode =>
    (rawItem.parentId && nodesById.get(rawItem.parentId)) || root;

  const linkChild = (rawItem: RawGraphNode2): void => {
    const node = nodesById.get(rawItem.id);
    if (!node) return;
    const parent = parentOf(rawItem);
    node.parent = parent;
    parent.children?.push(node);
  };

  for (const rawGroup of groupMap.values()) linkChild(rawGroup);
  for (const rawItem of componentMap.values()) linkChild(rawItem);

  const edges: BlocksReference[] = [];
  const drawnReferenceTypes: APIReferenceType[] = [];
  /**
   * The modernized Block Diagram's parent-child reference type. Both the drawn edge and its
   * legend row derive from it, so the line and the swatch cannot drift apart. Built once and
   * not per edge: every parent-child edge in a graph carries the same one.
   */
  const parentChildReferenceType = ParentChildGraphEdge.getType(i18nLocale);
  for (const rawReference of referenceList) {
    const { modelId, sourceId, targetId, isParentChildReference } =
      rawReference;
    if (!modelId) continue;
    const source = nodesById.get(sourceId);
    const target = nodesById.get(targetId);
    if (!source || !target) continue;
    const referenceType = isParentChildReference
      ? parentChildReferenceType
      : toReferenceType(scopeData, modelId);
    const edge: BlocksReference = {
      source,
      target,
      labels: toEdgeLabels(edgeLabelContext, rawReference),
      labelColors: [],
      modelId,
      urlFieldValues: null,
      globalTypeId: referenceUtils.getGlobalTypeId(scopeData, modelId),
      visualState: BlocksVisualState.None,
      ...(referenceType ? toEdgeStyle(referenceType) : DEFAULT_EDGE_STYLE),
    };
    edges.push(edge);
    if (referenceType) drawnReferenceTypes.push(referenceType);
    if (linkEdgesToNodes) {
      source.links?.push(edge);
      target.links?.push(edge);
    }
  }

  // Both maps, as `getGraphComponentLegendProps` does: a component-backed group
  // container is drawn and needs describing. It takes both filters to get there.
  // `isTruthy` drops the groups that carry no `modelId` at all - field-value and
  // "all" grouping, which `buildGroups` gives `undefined` - and `isComponent`
  // drops the ones whose id is a workspace, a component type or a tag.
  const drawnComponentIds = [...componentMap.values(), ...groupMap.values()]
    .map(({ modelId }) => modelId)
    .filter(isTruthy)
    .filter(modelId => graphInterface.isComponent(modelId));

  return {
    graph: { root, edges },
    referenceTypes: drawnReferenceTypes,
    componentIds: uniq(drawnComponentIds),
  };
};
